import axios, { AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';

import { camelizeKeys } from './camelize';
import { resolveBeEndpoint } from '../lib/endpointResolver';
import { resolveOrgFromHostname } from '../lib/org';
import { isPreviewIntent } from '../lib/preview';

// Shared axios for enrollment-be's public Finder API.
// `organization` header from hostname; 2 retries on 5xx/net.
const API_PREFIX = '/core/v1';
const baseURL = `${resolveBeEndpoint().replace(/\/+$/, '')}${API_PREFIX}`;
const org = resolveOrgFromHostname();

// Credentials only in preview intent (?preview=1) — the public finder stays
export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 15000,
  withCredentials: isPreviewIntent(),
  headers: {
    Accept: 'application/json',
    ...(org ? { organization: org } : {}),
  },
});

// BE reads the `route` header for analytics $current_url; without it the
// fallback is the API endpoint URL, not the SPA page.
apiClient.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    config.headers.set('route', window.location.href);
  }
  return config;
});

// Normalize BE payloads to camelCase once at the boundary so service
// adapters only worry about semantic renames (e.g. school_name → name).
apiClient.interceptors.response.use((response) => {
  response.data = camelizeKeys(response.data);
  return response;
});

axiosRetry(apiClient, {
  retries: 2,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => (
    axiosRetry.isNetworkError(error)
            || axiosRetry.isRetryableError(error)
            || (error.response?.status !== undefined && error.response.status >= 500)
  ),
});

export function getCurrentOrg(): string {
  return org;
}
